import React from "react";
import { CASES } from "@/lib/content";
import { ArrowRight, Sparkles } from "lucide-react";

function CaseCard({ caseItem, onBook }) {
    return (
        <div
            data-testid={`case-${caseItem.id}`}
            className="bg-white rounded-3xl overflow-hidden border border-[#e8e4db] shadow-sm"
        >
            <div className="grid grid-cols-2 relative">
                {/* Before */}
                <div className="relative overflow-hidden aspect-[4/5] bg-[#e8e4db]">
                    <img
                        src={caseItem.before.src}
                        alt={`${caseItem.name} — before treatment`}
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        aria-hidden="true"
                    />
                    {/* Privacy strip: horizontal black bar over the eyes only */}
                    <div
                        className="absolute inset-x-0 bg-[#1a1d1c]"
                        style={{
                            top: `${caseItem.before.barTop}%`,
                            height: `${caseItem.before.barHeight}%`,
                        }}
                        aria-hidden="true"
                    />
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#faf9f6] bg-[#1a1d1c]/80 backdrop-blur px-3 py-1 rounded-full z-10">
                        Before
                    </div>
                </div>

                {/* After */}
                <div className="relative overflow-hidden aspect-[4/5] bg-[#e8e4db]">
                    <img
                        src={caseItem.after.src}
                        alt={`${caseItem.name} — after treatment`}
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        aria-hidden="true"
                    />
                    <div
                        className="absolute inset-x-0 bg-[#1a1d1c]"
                        style={{
                            top: `${caseItem.after.barTop}%`,
                            height: `${caseItem.after.barHeight}%`,
                        }}
                        aria-hidden="true"
                    />
                    <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-[#faf9f6] bg-[#114b5f]/90 backdrop-blur px-3 py-1 rounded-full flex items-center gap-1.5 z-10">
                        <Sparkles size={10} strokeWidth={2} className="text-[#d4af37]" />
                        After
                    </div>
                </div>

                {/* Center divider line */}
                <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-[#faf9f6]/60" />
            </div>

            <div className="p-6 md:p-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                    {caseItem.treatment}
                </div>
                <h3 className="mt-2 font-serif-display text-2xl text-[#1a1d1c]">
                    {caseItem.name}&apos;s smile transformation
                </h3>
                <p className="mt-3 text-sm md:text-base text-[#4a5250] leading-relaxed">
                    {caseItem.summary}
                </p>
                <button
                    type="button"
                    onClick={onBook}
                    data-testid={`case-cta-${caseItem.id}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#114b5f] hover:text-[#0d3a4a] font-medium"
                >
                    Book a smile consultation
                    <ArrowRight
                        size={14}
                        strokeWidth={1.75}
                        className="transition group-hover:translate-x-0.5"
                    />
                </button>
            </div>
        </div>
    );
}

export default function SmileTransformations({ onBook }) {
    return (
        <section
            id="cases"
            data-testid="cases-section"
            className="py-24 md:py-32 bg-[#faf9f6]"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="max-w-2xl mb-14">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#7a8582] flex items-center">
                        <span className="gdc-divider" />
                        Real transformations
                    </div>
                    <h2 className="font-serif-display mt-4 text-4xl md:text-5xl leading-tight text-[#1a1d1c]">
                        Before &amp; after,
                        <br />
                        <span className="italic text-[#114b5f]">
                            straight from our chair.
                        </span>
                    </h2>
                    <p className="mt-5 text-[#4a5250] max-w-xl">
                        Real patients, real results — shared with consent.
                        Names marked with * have been changed and
                        identifying features discreetly masked to respect
                        patient privacy.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {CASES.map((c) => (
                        <CaseCard
                            key={c.id}
                            caseItem={c}
                            onBook={() => onBook?.()}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
